import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';

import { DumbTask } from '../DumbTask';
import { useTaskButtonProps } from '../useTaskButtonProps';
import type { OnboardingTask_taskInterface } from './__generated__/index.graphql';

const BottomAlignedSBHorizontal = styled(SBHorizontal)`
  align-items: flex-end;
`;

type Props = {
  onboardingTask: OnboardingTask_taskInterface;
  borderColor?: ComponentProps<typeof DumbTask>['borderColor'];
};
export const OnboardingTask = ({ onboardingTask, borderColor }: Props) => {
  const { to, label } = useTaskButtonProps({
    task: onboardingTask,
  });
  const { description, rewardConfigs } = onboardingTask;
  const reward = rewardConfigs.at(0);
  const track = useEvents();

  return (
    <DumbTask
      featured
      borderColor={borderColor}
      description={<BodyS color="var(--c-nd-600)">{description}</BodyS>}
      footer={
        <BottomAlignedSBHorizontal>
          <div>
            {!!to && !!label && (
              <Button
                size="small"
                color="secondary"
                onClick={() => {
                  track('Click Mission Button', {
                    slug: onboardingTask.taskConfigSlug,
                    onboarding: true,
                  });
                }}
                to={to}
              >
                {label}
              </Button>
            )}
          </div>
          <div>
            {!!reward && (
              <AnyRewardConfig
                anyRewardConfig={reward}
                variant={Variant.CHIP}
              />
            )}
          </div>
        </BottomAlignedSBHorizontal>
      }
    />
  );
};

OnboardingTask.fragments = {
  taskInterface: gql`
    fragment OnboardingTask_taskInterface on TaskInterface {
      id
      iconUrl
      title
      description
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ...useTaskButtonProps_taskInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${useTaskButtonProps.fragments.taskInterface}
  ` as TypedDocumentNode<OnboardingTask_taskInterface>,
};
